import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import Loader from "@jumbo/components/Loader";
import DialogConfirm from "@jumbo/components/JumboDialog/components/DialogConfirm";
import { useDisclosure } from "@jumbo/components/JumboDialog/hooks/useDisclosure";
import { Table } from "app/components";
import { userType } from "app/helpers/userType";
import { usePagination } from "app/hooks/usePagination";
import {
  clearDetail,
  clearPreview,
  clearStockOut,
  discardLabelImport,
  downloadImportPdf,
  getLabelImportById,
  deleteLabelImport,
  getLabelImportHistory,
  getStockOutPlan,
  parseLabelPdf,
  runStockOut,
  saveLabelImport,
  undoStockOut,
  updateImportQuantities,
} from "app/redux/slice/orderLabelSlice";
import ReportTables from "./ReportTables";
import ImportDetailDrawer from "./ImportDetailDrawer";
import StockOutDrawer from "./StockOutDrawer";
import { useQuantityEdits } from "./useQuantityEdits";
import { useEditMode } from "./useEditMode";
import BusyOverlay from "./BusyOverlay";
import { PLATFORMS, QUICK_RANGES, STATUSES, platformLabel } from "./constants";

const emptyFilters = { platform: "", status: "" };

const OrderLabelImport = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);

  const {
    loading,
    saving,
    historyLoading,
    preview,
    detail,
    history,
    stockOut,
    pdfLoading,
  } = useSelector((state) => state?.orderLabel) ?? {};

  /**
   * Every action on this page is allowed separately: reaching the page is not
   * permission to bring a PDF in, and neither is it permission to move stock.
   * The API enforces all of it - hiding a control only spares the user a
   * button that would be refused.
   */
  const { isMA } = userType();
  const can = useMemo(() => {
    const stored = JSON.parse(localStorage.getItem("permission")) ?? [];
    const has = (name) => isMA || stored.some((item) => item?.name === name);
    return {
      upload: has("order-label-upload"),
      save: has("order-label-save"),
      update: has("order-label-update"),
      stockOut: has("order-label-stock-out"),
      remove: has("order-label-delete"),
    };
  }, [isMA]);

  // Someone who may only read lands on the history: the upload tab would have
  // nothing on it they are allowed to do.
  const [activeTab, setActiveTab] = useState(() =>
    isMA ||
    (JSON.parse(localStorage.getItem("permission")) ?? []).some(
      (item) => item?.name === "order-label-upload"
    )
      ? "upload"
      : "history"
  );

  // ---- Upload tab ----
  const [platform, setPlatform] = useState(3);
  const [file, setFile] = useState(null);

  // ---- History tab filters (staged, applied on Apply Filters) ----
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [tempDateRange, setTempDateRange] = useState([null, null]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [tempFilters, setTempFilters] = useState(emptyFilters);
  const [filters, setFilters] = useState(emptyFilters);

  const [detailOpen, setDetailOpen] = useState(false);
  // The stock out opens on its own, from the history row - it never needs the
  // report tables to be loaded first.
  const [stockOutOpen, setStockOutOpen] = useState(false);
  const [stockOutRow, setStockOutRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const confirmDelete = useDisclosure();

  // Cell edits made on the preview, before it is saved.
  const editMode = useEditMode();
  const { editing } = editMode;
  const previewEdits = useQuantityEdits();

  /**
   * Two things stop an import being stored, because either would make the
   * saved totals miss part of the PDF: a size that belongs to no chart, and a
   * SKU that resolves to no product. The first also blocks editing - the
   * tables themselves are incomplete - while unmapped SKUs only block saving.
   */
  const excludedRows = preview?.summary?.excludedRows ?? 0;
  const unmappedSkus = preview?.unmapped?.length ?? 0;

  const blockedByExcluded = excludedRows > 0;
  const editBlockedReason = blockedByExcluded
    ? `${excludedRows} order(s) have no size chart. Add their sizes to a size chart, then parse the PDF again.`
    : // Editing a preview only decides what gets stored, so it is the right to
    // save that governs it.
    !can.save
    ? "You do not have permission to save an import."
    : "";
  const saveBlockedReason =
    editBlockedReason ||
    (unmappedSkus > 0
      ? `${unmappedSkus} SKU(s) are still unmapped. Add them to a product, then parse the PDF again.`
      : "");
  const saveBlocked = Boolean(saveBlockedReason);
  const editBlocked = blockedByExcluded || !can.save;

  /**
   * What the tables add up to with the pending edits applied.
   *
   * It starts from the total the server already worked out, not from the row
   * count: a cell holds pieces, and one line of a Myntra picklist can carry
   * several of them.
   */
  const adjustedRows = useMemo(() => {
    if (!preview) return 0;
    const allRows = [...(preview.mapped ?? []), ...(preview.unmapped ?? [])];
    const shownOf = (sku, size) =>
      allRows.find((row) => row.sku === sku)?.sizes?.[size] || 0;

    return previewEdits.overrides.reduce(
      (sum, item) =>
        sum + (Number(item.quantity) - shownOf(item.sku, item.size)),
      preview.summary?.adjustedRows ?? 0
    );
  }, [preview, previewEdits.overrides]);

  // Newest import first - a history reads by recency, not by size.
  const [orderBy, setOrderBy] = useState("date");
  const [order, setOrder] = useState("desc");

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "order-label-history", pageSize: 10 });

  const loadHistory = (pageNumber = page) => {
    const params = {
      page: pageNumber,
      perPage: rowsPerPage,
      field: orderBy,
      sort: order === "asc" ? 1 : -1,
    };
    if (search) params.search = search;
    if (filters.platform) params.platform = filters.platform;
    if (filters.status) params.status = filters.status;
    if (dateRange[0])
      params.fromDate = moment(dateRange[0]).format("YYYY-MM-DD");
    if (dateRange[1]) params.toDate = moment(dateRange[1]).format("YYYY-MM-DD");
    dispatch(getLabelImportHistory(params));
  };

  // Only the visible tab fetches.
  useEffect(() => {
    if (activeTab === "history") loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    page,
    rowsPerPage,
    search,
    dateRange,
    filters,
    order,
    orderBy,
  ]);

  // The shared table owns the sort UI and reports the change back; the list
  // itself is paged and sorted by the API, so this only records it.
  const sortData = (nextOrder, nextOrderBy) => {
    setOrder(nextOrder);
    setOrderBy(nextOrderBy);
  };

  const resetFileInput = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  /**
   * A parsed-but-unsaved upload still holds a PDF on the server, so walking
   * away from it has to tell the server to drop the file.
   */
  const dropPreview = () => {
    if (preview?.parseToken) {
      dispatch(discardLabelImport({ parseToken: preview.parseToken }));
    }
    dispatch(clearPreview());
    resetFileInput();
    editMode.reset();
    previewEdits.reset();
  };

  useEffect(() => {
    return () => {
      dispatch(clearPreview());
      dispatch(clearDetail());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Same reason as dropPreview, for a browser tab being closed or reloaded.
  useEffect(() => {
    const token = preview?.parseToken;
    if (!token) return undefined;
    const onUnload = () => {
      dispatch(discardLabelImport({ parseToken: token }));
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview?.parseToken]);

  const onChangeTab = (_, value) => {
    if (value === activeTab) return;
    if (value === "history" && preview?.parseToken) dropPreview();
    setActiveTab(value);
  };

  const onParse = () => {
    if (!file) {
      enqueueSnackbar("Please choose a PDF file.", { variant: "warning" });
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    formData.append("platform", platform);

    // A fresh parse starts from the PDF, so any pending edits go with it.
    editMode.reset();
    previewEdits.reset();

    dispatch(parseLabelPdf(formData))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res?.data?.responseMessage, { variant: "success" });
        } else {
          enqueueSnackbar(
            res?.data?.responseMessage ?? "Could not read this PDF.",
            { variant: "error" }
          );
        }
      });
  };

  const onSave = () => {
    dispatch(
      saveLabelImport({
        parseToken: preview.parseToken,
        platform: preview.platform,
        originalFileName: preview.originalFileName,
        overrides: previewEdits.overrides,
      })
    )
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res?.data?.responseMessage, { variant: "success" });
          dispatch(clearPreview());
          resetFileInput();
          editMode.reset();
          previewEdits.reset();
          setActiveTab("history");
          handleChangePage(0, 0);
        } else {
          enqueueSnackbar(
            res?.data?.responseMessage ?? "Could not save this import.",
            { variant: "error" }
          );
        }
      });
  };

  const applyFilters = () => {
    setDateRange(tempDateRange);
    setFilters(tempFilters);
    setSearch(searchText.trim());
    handleChangePage(0, 0);
  };

  const clearFilters = () => {
    setTempDateRange([null, null]);
    setDateRange([null, null]);
    setTempFilters(emptyFilters);
    setFilters(emptyFilters);
    setSearchText("");
    setSearch("");
    handleChangePage(0, 0);
  };

  const openDetail = (id) => {
    dispatch(getLabelImportById(id));
    setDetailOpen(true);
  };

  const openStockOut = (row) => {
    setStockOutRow(row);
    dispatch(getStockOutPlan(row._id));
    setStockOutOpen(true);
  };

  const closeStockOut = () => {
    setStockOutOpen(false);
    setStockOutRow(null);
    dispatch(clearStockOut());
  };

  const closeDetail = () => {
    setDetailOpen(false);
    dispatch(clearDetail());
  };

  /**
   * Both stock out actions re-read everything afterwards: the plan becomes the
   * record (or the other way round), the report's edit state changes with it,
   * and the history row shows the new status.
   */
  const refreshAfterStockOut = (id) => {
    dispatch(getStockOutPlan(id));
    // The history row carries the status, and the report drawer reads its
    // "already stocked out" rule from the same place - so it is only reloaded
    // when it happens to be showing this import.
    loadHistory();
    if (detail?.history?._id === id) dispatch(getLabelImportById(id));
  };

  const runAction = (thunk, fallbackMessage, arg) => {
    const id = stockOutRow?._id;
    if (!id) return;
    dispatch(thunk(arg === undefined ? id : arg))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res?.data?.responseMessage, { variant: "success" });
          refreshAfterStockOut(id);
        } else {
          enqueueSnackbar(res?.data?.responseMessage ?? fallbackMessage, {
            variant: "error",
          });
          // A refusal usually means the data moved under us - reload so the
          // screen shows why rather than the stale plan.
          dispatch(getStockOutPlan(id));
        }
      });
  };

  const onStockOut = (date) =>
    runAction(runStockOut, "Could not stock out.", {
      id: stockOutRow?._id,
      date,
    });

  /**
   * Deleting takes the import and everything saved with it off every screen.
   * The list is read again afterwards rather than the row being removed
   * here, so the paging and the total stay honest.
   */
  const onDeleteImport = () => {
    const row = deleteRow;
    if (!row?._id) return;
    confirmDelete.onClose();
    setDeleting(true);
    dispatch(deleteLabelImport(row._id))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res?.data?.responseMessage, { variant: "success" });
          // It may have been the last row of the page.
          const wasLastOnPage = (history?.list?.length ?? 0) === 1;
          if (wasLastOnPage && page > 1) handleChangePage(null, page - 2);
          else loadHistory();
          if (stockOutRow?._id === row._id) closeStockOut();
          if (detail?.history?._id === row._id) closeDetail();
        } else {
          enqueueSnackbar(
            res?.data?.responseMessage ?? "Could not delete this import.",
            { variant: "error" }
          );
          loadHistory();
        }
      })
      .finally(() => {
        setDeleting(false);
        setDeleteRow(null);
      });
  };

  const onUndoStockOut = () =>
    runAction(undoStockOut, "Could not undo the stock out.", {
      id: stockOutRow?._id,
    });

  // One variant back, leaving the rest of the import where it is.
  const onRevertVariant = (record) =>
    runAction(undoStockOut, "Could not revert this variant.", {
      id: stockOutRow?._id,
      recordIds: [record._id],
    });

  /**
   * `id` doubles as the API's sort field and the row property, which is why
   * the column ids match the stored names rather than the labels.
   */
  const columns = [
    {
      id: "date",
      label: "Date",
      sort: true,
      minWidth: 150,
      render: (row) => moment(row.date).format("DD/MM/YYYY HH:mm"),
    },
    {
      id: "platform",
      label: "Platform",
      sort: true,
      render: (row) => platformLabel(row.platform),
    },
    { id: "originalFileName", label: "File", sort: true, minWidth: 220 },
    {
      id: "status",
      label: "Status",
      sort: true,
      render: (row) => (
        <Chip
          size="small"
          variant="outlined"
          color={
            row.status === "completed"
              ? "success"
              : row.status === "failed"
              ? "error"
              : "warning"
          }
          label={row.status}
        />
      ),
    },
    { id: "totalPages", label: "Pages", sort: true },
    {
      id: "totalRows",
      label: "Rows",
      sort: true,
      render: (row) => (
        <Box sx={{ whiteSpace: "nowrap" }}>
          {row.totalRows}
          {/* An edited import still reports what the PDF said; the corrected
              figure rides alongside it. */}
          {row.isEdited && (
            <Tooltip
              title={`Edited by ${row.editedBy?.name ?? "-"} - ${
                row.editedCells
              } cell(s), now ${row.adjustedRows}`}
            >
              <Box
                component="span"
                sx={{
                  ml: 0.5,
                  color: "warning.main",
                  fontWeight: "bold",
                  cursor: "help",
                }}
              >
                ✎ {row.adjustedRows}
              </Box>
            </Tooltip>
          )}
        </Box>
      ),
    },
    { id: "mappedRows", label: "Mapped", sort: true },
    {
      id: "unmappedRows",
      label: "Unmapped",
      sort: true,
      render: (row) => (
        <Box
          component="span"
          sx={{ color: row.unmappedRows ? "error.main" : "inherit" }}
        >
          {row.unmappedRows}
        </Box>
      ),
    },
    {
      id: "stockOutStatus",
      label: "Stock Out",
      sort: true,
      render: (row) =>
        row.stockOutStatus === "done" ? (
          <Tooltip
            title={`Stocked out${
              row.stockOutAt
                ? " on " + moment(row.stockOutAt).format("DD/MM/YYYY HH:mm")
                : ""
            }${row.stockOutBy?.name ? " by " + row.stockOutBy.name : ""}`}
          >
            <Box component="span" sx={{ display: "inline-block" }}>
              <Chip size="small" color="success" label="Done" />
              {row.stockOutAt && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", whiteSpace: "nowrap", mt: 0.25 }}
                >
                  {moment(row.stockOutAt).format("DD/MM/YYYY")}
                </Typography>
              )}
            </Box>
          </Tooltip>
        ) : (
          <Chip size="small" variant="outlined" label="Pending" />
        ),
    },
    {
      id: "importedBy",
      label: "Imported By",
      render: (row) => row.importedBy?.name ?? "-",
    },
    {
      id: "actions",
      label: "",
      align: "right",
      render: (row) => (
        <Box sx={{ whiteSpace: "nowrap" }}>
          <IconButton
            size="small"
            title="View tables"
            onClick={() => openDetail(row._id)}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            title={
              row.stockOutStatus === "done"
                ? "View / undo stock out"
                : "Stock out"
            }
            color={row.stockOutStatus === "done" ? "success" : "default"}
            onClick={() => openStockOut(row)}
          >
            <RemoveShoppingCartIcon fontSize="small" />
          </IconButton>
          {/* Only while the stock is still in the app: once it is out,
              this import is the only record of why, so it has to be
              undone before it can go. */}
          {can.remove && row.stockOutStatus !== "done" && (
            <IconButton
              size="small"
              title="Delete this import"
              color="error"
              disabled={deleting}
              onClick={() => {
                setDeleteRow(row);
                confirmDelete.onOpen();
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  /**
   * The server sends the report back as base64 rather than a file stream, the
   * same way the app's inventory report does, so it is turned into a blob here
   * and handed to the browser.
   */
  const onDownloadPdf = () => {
    const id = detail?.history?._id;
    if (!id) return;
    dispatch(downloadImportPdf(id))
      .unwrap()
      .then((res) => {
        const data = res?.data?.data;
        if (res?.status !== 200 || !data?.base64Pdf) {
          enqueueSnackbar(
            res?.data?.responseMessage ?? "Could not build the PDF.",
            { variant: "error" }
          );
          return;
        }

        const bytes = Uint8Array.from(atob(data.base64Pdf), (char) =>
          char.charCodeAt(0)
        );
        const url = URL.createObjectURL(
          new Blob([bytes], { type: "application/pdf" })
        );
        const link = document.createElement("a");
        link.href = url;
        link.download = data.fileName ?? "label-import-report.pdf";
        link.click();
        URL.revokeObjectURL(url);
      });
  };

  const onSaveQuantities = (overrides) => {
    const id = detail?.history?._id;
    if (!id) return;
    dispatch(updateImportQuantities({ id, overrides }))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res?.data?.responseMessage, { variant: "success" });
          // Re-read so the drawer and the history row agree again. The stock
          // out plan is built from these quantities, so it moves with them.
          dispatch(getLabelImportById(id));
          loadHistory();
          // The plan is built from these quantities, so it moves with them.
          if (stockOutRow?._id === id) dispatch(getStockOutPlan(id));
        } else {
          enqueueSnackbar(
            res?.data?.responseMessage ?? "Could not update quantities.",
            { variant: "error" }
          );
        }
      });
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Label PDF Import
      </Typography>

      <Tabs
        value={activeTab}
        onChange={onChangeTab}
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        {can.upload && <Tab value="upload" label="Upload File" />}
        <Tab value="history" label="Import History" />
      </Tabs>

      {/* ==================== UPLOAD ==================== */}
      {activeTab === "upload" && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Platform"
                    value={platform}
                    onChange={(e) => setPlatform(Number(e.target.value))}
                  >
                    {PLATFORMS.map((p) => (
                      <MenuItem
                        key={p.value}
                        value={p.value}
                        disabled={!p.supported}
                      >
                        {p.label}
                        {!p.supported && " (coming soon)"}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Button
                    fullWidth
                    variant="outlined"
                    component="label"
                    startIcon={<UploadFileIcon />}
                    sx={{ justifyContent: "flex-start", textTransform: "none" }}
                  >
                    {file ? file.name : "Choose label PDF"}
                    <input
                      ref={fileInputRef}
                      hidden
                      type="file"
                      accept="application/pdf,.pdf"
                      onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                    />
                  </Button>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Stack direction="row" spacing={1}>
                    <LoadingButton
                      fullWidth
                      variant="contained"
                      size="small"
                      loading={loading}
                      onClick={onParse}
                      startIcon={<PictureAsPdfIcon />}
                    >
                      Parse PDF
                    </LoadingButton>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      disabled={!file && !preview}
                      onClick={dropPreview}
                    >
                      Reset
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {loading && <Loader />}

          {!loading && preview && (
            <Card>
              <CardContent>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  alignItems={{ md: "center" }}
                  justifyContent="space-between"
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ mb: 0.5 }}>
                      {preview.originalFileName}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
                      <Chip
                        size="small"
                        label={platformLabel(preview.platform)}
                      />
                      <Chip
                        size="small"
                        label={`${preview.totalPages} pages`}
                      />
                      <Chip
                        size="small"
                        label={`${preview.summary?.totalRows ?? 0} rows parsed`}
                      />
                      {/* A line can be more than one piece, so the two numbers
                          are shown apart when they differ. */}
                      {preview.summary?.totalQuantity !==
                        preview.summary?.totalRows && (
                        <Chip
                          size="small"
                          label={`${
                            preview.summary?.totalQuantity ?? 0
                          } pieces`}
                        />
                      )}
                      {previewEdits.hasChanges && (
                        <Chip
                          size="small"
                          color="warning"
                          label={`${adjustedRows} after edits`}
                        />
                      )}
                      <Chip
                        size="small"
                        color="success"
                        variant="outlined"
                        label={`${preview.summary?.mappedRows ?? 0} mapped`}
                      />
                      <Chip
                        size="small"
                        color={
                          preview.summary?.unmappedRows ? "error" : "default"
                        }
                        variant="outlined"
                        label={`${preview.summary?.unmappedRows ?? 0} unmapped`}
                      />
                    </Stack>
                  </Box>

                  <Stack direction="row" spacing={1}>
                    {/* Blocked while any order has no column: saving would
                        store a picture that leaves those orders out. The span
                        is what lets the tooltip work on disabled buttons.
                        Discard stays enabled - it is the way out. */}
                    <Tooltip title={editBlockedReason}>
                      <span>
                        <Button
                          variant={editing ? "contained" : "outlined"}
                          color={editing ? "warning" : "primary"}
                          startIcon={<EditIcon />}
                          onClick={editMode.toggle}
                          disabled={editMode.switching || editBlocked}
                        >
                          {editing ? "Done Editing" : "Edit"}
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title={saveBlockedReason}>
                      <span>
                        <LoadingButton
                          variant="contained"
                          loading={saving}
                          onClick={onSave}
                          disabled={saveBlocked}
                        >
                          Save Import
                        </LoadingButton>
                      </span>
                    </Tooltip>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={dropPreview}
                    >
                      Discard
                    </Button>
                  </Stack>
                </Stack>

                <Alert
                  severity={previewEdits.hasChanges ? "warning" : "info"}
                  sx={{ mb: 2 }}
                >
                  {previewEdits.hasChanges ? (
                    <>
                      <b>{previewEdits.overrides.length} cell(s) edited.</b> The
                      PDF said <b>{preview.summary?.totalQuantity ?? 0}</b>{" "}
                      piece(s), these tables now total <b>{adjustedRows}</b>.
                      Press
                      <b> Save Import </b>to keep the edited figures.
                    </>
                  ) : (
                    <>
                      Nothing is stored yet. Review the tables below and press
                      <b> Save Import </b>to keep this data.
                    </>
                  )}
                </Alert>

                <BusyOverlay
                  busy={editMode.switching}
                  label={editing ? "Closing editor..." : "Opening editor..."}
                >
                  <ReportTables
                    report={preview}
                    editing={editing}
                    edits={previewEdits.edits}
                    onCellChange={previewEdits.setCell}
                    onCellReset={previewEdits.resetCell}
                  />
                </BusyOverlay>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ==================== HISTORY ==================== */}
      {activeTab === "history" && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search file name"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") applyFilters();
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          sx={{
                            display: searchText === "" ? "none" : "flex",
                            cursor: "pointer",
                          }}
                          onClick={() => setSearchText("")}
                        >
                          <ClearIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateRangePicker
                      startText="From Date"
                      endText="To Date"
                      inputFormat="dd/MM/yyyy"
                      value={tempDateRange}
                      onChange={setTempDateRange}
                      renderInput={(
                        { inputProps, ...startProps },
                        endProps
                      ) => {
                        const startValue = inputProps.value;
                        delete inputProps.value;
                        return (
                          <TextField
                            {...startProps}
                            inputProps={inputProps}
                            value={`${startValue || "DD/MM/YYYY"} - ${
                              endProps.inputProps.value || "DD/MM/YYYY"
                            }`}
                            fullWidth
                            size="small"
                          />
                        );
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12} md={3}>
                  <ButtonGroup size="small" fullWidth variant="outlined">
                    {QUICK_RANGES.map((r) => (
                      <Button
                        key={r.key}
                        onClick={() =>
                          setTempDateRange([
                            moment()
                              .subtract(r.back, "days")
                              .startOf("day")
                              .toDate(),
                            moment().endOf("day").toDate(),
                          ])
                        }
                      >
                        {r.label}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Grid>

                <Grid item xs={6} md={2}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Platform"
                    value={tempFilters.platform}
                    onChange={(e) =>
                      setTempFilters((p) => ({
                        ...p,
                        platform: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">All</MenuItem>
                    {PLATFORMS.map((p) => (
                      <MenuItem key={p.value} value={p.value}>
                        {p.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={6} md={2}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Status"
                    value={tempFilters.status}
                    onChange={(e) =>
                      setTempFilters((p) => ({ ...p, status: e.target.value }))
                    }
                  >
                    <MenuItem value="">All</MenuItem>
                    {STATUSES.map((s) => (
                      <MenuItem key={s.value} value={s.value}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={2}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      fullWidth
                      variant="contained"
                      size="small"
                      onClick={applyFilters}
                    >
                      Apply
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={clearFilters}
                    >
                      Clear
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              {historyLoading ? (
                <Loader />
              ) : (
                <Table
                  columns={columns}
                  rows={history?.list ?? []}
                  count={history?.total ?? 0}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  Order={order}
                  OrderBy={orderBy}
                  sortData={sortData}
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                />
              )}
            </CardContent>
          </Card>
        </>
      )}

      <ImportDetailDrawer
        open={detailOpen}
        loading={loading}
        saving={saving}
        detail={detail}
        pdfLoading={pdfLoading}
        canUpdate={can.update}
        onClose={closeDetail}
        onSaveQuantities={onSaveQuantities}
        onDownloadPdf={onDownloadPdf}
      />

      <DialogConfirm
        isOpen={confirmDelete.isOpen}
        onClose={() => {
          confirmDelete.onClose();
          setDeleteRow(null);
        }}
        title="Delete this import?"
        content={
          <>
            <b>{deleteRow?.originalFileName}</b> and everything saved with it -{" "}
            {deleteRow?.totalRows ?? 0} label row(s)
            {deleteRow?.editedCells
              ? ` and ${deleteRow.editedCells} edited cell(s)`
              : ""}{" "}
            - will be removed from every screen. No stock moves: this import has
            not been stocked out.
          </>
        }
        onConfirm={onDeleteImport}
        btnLabels={{ cancel: "Cancel", confirm: "Delete" }}
        confirmBtnProps={{ color: "error" }}
      />

      <StockOutDrawer
        open={stockOutOpen}
        row={stockOutRow}
        stockOut={stockOut}
        canStockOut={can.stockOut}
        onClose={closeStockOut}
        onStockOut={onStockOut}
        onUndo={onUndoStockOut}
        onRevert={onRevertVariant}
      />
    </Box>
  );
};

export default OrderLabelImport;
